import { getConditionLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { toast } from "@shared/ui/use-toast";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAssetList, useDeleteAsset } from "../hooks";
import { AssetsFilterPanel } from "./AssetsFilterPanel";

// Asset interface
interface Asset {
  id: string;
  name: string;
  type: string;
  class: string;
  location: string;
  systemNetwork: string;
  age: number;
  operationalStatus: string;
  conditionScore: number;
  riskScore: number;
  replacementCost: number;
  lastInspectionDate: string;
}

interface AssetFilters {
  type?: string;
  status?: string;
}

export const AssetsTable: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<any>(null);
  const [isDeletingSystem, setIsDeletingSystem] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);

  const { mutate: deleteAssetMutation, isPending: isDeletingAsset } =
    useDeleteAsset();

  const currency = useCurrency();
  useEffect(() => {
    logEvent("Assets Table Viewed", {
      timestamp: new Date().toISOString(),
    });
  }, []);
  // Parse URL parameters for filters and pagination
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      type: searchParams.get("type") || undefined,
      status: searchParams.get("status") || undefined,
      page: parseInt(searchParams.get("page") || "1", 10),
      search: searchParams.get("search") || "",
    };
  }, [location.search]);

  const [currentPage, setCurrentPage] = useState(urlParams.page);
  const [filters, setFilters] = useState<AssetFilters>({
    type: urlParams.type,
    status: urlParams.status,
  });

  // Update state when URL changes
  useEffect(() => {
    setCurrentPage(urlParams.page);
    setFilters({
      type: urlParams.type,
      status: urlParams.status,
    });
    setSearchTerm(urlParams.search);
  }, [urlParams]);

  // Format filters for the API hook
  const formatFiltersForHook = (filters: AssetFilters) => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }

    // Add sorting
    if (sortField) {
      hookFilters.sort = sortField;
    }

    // Add filters
    if (filters.type) {
      hookFilters.asset_type_code = filters.type;
    }
    if (filters.status) {
      hookFilters.status = filters.status;
    }

    logEvent("Dashboard view", {
      timestamp: new Date().toISOString(),
      filterApplied: hookFilters,
      dashboardType: "asset",
    });

    return hookFilters;
  };

  const { data: assetData, isLoading } = useAssetList(
    formatFiltersForHook(filters)
  );

  // Helper functions for badge colors and text
  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Service":
        return "bg-green-100 text-green-800";
      case "Standby":
        return "bg-yellow-100 text-yellow-800";
      case "Out of Service":
        return "bg-red-100 text-red-800";
      case "Under Maintenance":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getConditionColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined)
      return "bg-gray-100 text-gray-800"; // NA
    if (score > 80) return "bg-green-100 text-green-800"; // Good
    if (score >= 40) return "bg-yellow-100 text-yellow-800"; // Fair
    return "bg-red-100 text-red-800"; // Poor
  };

  const getConditionText = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "NA";
    if (score > 80) return "Good";
    if (score >= 40) return "Fair";
    return "Poor";
  };

  const getRiskColor = (score: number) => {
    if (score >= 70) return "bg-red-100 text-red-800";
    if (score >= 40) return "bg-yellow-100 text-yellow-800";
    return "bg-green-100 text-green-800";
  };

  const getRiskText = (score: any) => {
    if (score >= 70) return "High";
    if (score >= 40) return "Medium";
    if (score === null) return "NA";
    return "Low";
  };

  const handleDeleteClick = (system: any) => {
    setAssetToDelete(system);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (assetToDelete) {
      logEvent("Asset Delete Confirmed", {
        facilityId: assetToDelete.id,
      });
      deleteAssetMutation(
        { assetId: assetToDelete.id },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: `"${assetToDelete.assetName}" has been deleted successfully.`,
            });
            setShowDeleteDialog(false);
            setAssetToDelete(null);
          },
          onError: (error) => {
            toast({
              title: "Delete Failed",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setAssetToDelete(null);
  };

  const columns: TableColumn<Asset>[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "text",
      headerTooltip: "The unique identifier for this asset.",
    },
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
      headerTooltip: "The common name used to identify this asset.",
    },
    {
      key: "assetTypeDisplay",
      header: "Asset Type",
      type: "text",
      headerTooltip: "The general category this asset belongs to.",
    },
    {
      key: "assetClassDisplay",
      header: "Asset Class",
      type: "text",
      headerTooltip:
        "A more detailed classification for grouping and analysis.",
    },
    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
      headerTooltip: "The physical location or address of this asset.",
    },
    {
      key: "systemNetwork",
      header: "System/Network",
      type: "text",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {row.systemName ?? row.networkName ?? "NA"}
          </span>
        </div>
      ),
      headerTooltip:
        "The parent system or network this asset is a component of.",
    },
    {
      key: "currentAge",
      header: "Age",
      type: "custom",
      render: (value: number) => `${value} years`,
      headerTooltip: "The age of the asset since its installation date.",
    },
    {
      key: "replacementCost",
      header: "Replacement Cost",
      type: "custom",
      render: (value: number) => `${currency} ${value?.toLocaleString()}`,
      headerTooltip:
        "The estimated capital cost to replace this asset with a new one.",
    },
    {
      key: "lastInspectionDate",
      header: "Last Inspection Date",
      type: "text",
      headerTooltip:
        "The date the last formal inspection was completed for this asset.",
    },
    {
      key: "conditionScores",
      header: "Condition Score",
      type: "custom",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getConditionLabel(value).color}>
            {getConditionLabel(value).label}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The physical health of the asset, based on the most recent inspection. Higher is better.",
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getRiskColor(value)}>
            {row.riskStatus}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure.",
    },
    {
      key: "statusDisplay",
      header: "Operational Status",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
        variant: "secondary",
      },
      headerTooltip: "The current operational status of the asset.",
    },
  ];

  // Table actions
  const actions: TableAction<Asset>[] = [
    {
      label: "View Details",
      onClick: (row) => {
        navigate(`/ax/asset-registry/assets/view/${row.id}`);
        logEvent("Asset Viewed", { id: row.id });
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Edit",
      onClick: (row) => {
        navigate(`/ax/asset-registry/assets/create/edit/${row.id}`);
        logEvent("Asset Edit Clicked", { id: row.id });
      },
      icon: "Pen",
      variant: "outline",
    },
    {
      label: "Delete",
      onClick: (item) => handleDeleteClick(item),
    },
  ];

  // Update URL with new parameters
  const updateUrl = (
    updates: Partial<{
      page: number;
      search: string;
      type?: string;
      status?: string;
    }>
  ) => {
    const searchParams = new URLSearchParams(location.search);

    // Update parameters
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.set(key, String(value));
      } else {
        searchParams.delete(key);
      }
    });

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Event handlers
  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    updateUrl({ search: searchValue, page: 1 });
    setCurrentPage(1);
    logEvent("Assets Search Performed", { query: searchValue });
  };

  const handleFiltersChange = (newFilters: AssetFilters) => {
    logEvent("Assets Filters Applied", newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm("");
    setSortField(null);
    setSortConfig(null);
    setCurrentPage(1);
    logEvent("Assets Filters Cleared", {});
    // Clear all filter-related URL parameters
    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrl({ page });
    logEvent("Assets Page Changed", { page });
  };

  // Calculate pagination info
  const totalItems = assetData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      <GenericTable
        data={assetData?.results || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search assets by Name, Id"
        searchParamName="query"
        searchDebounceMs={300}
        searchValue={searchTerm}
        onSearch={handleSearch}
        sortConfig={sortConfig}
        onExport={() => {}}
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No assets found"
        filterPanel={
          <AssetsFilterPanel
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleFiltersChange}
          />
        }
      />

      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete Asset"
        itemName={assetToDelete?.assetName}
        description={`Are you sure you want to delete the asset "${assetToDelete?.assetName}" (ID: ${assetToDelete?.assetNo})? This will permanently remove the asset and all its associated data. This action cannot be undone.`}
        isLoading={isDeletingSystem}
      />
    </div>
  );
};
